




































































































import mixins from 'vue-typed-mixins'
import Section from '@/builder/sections/mixins/Section'
import TimeZoneText from '@/builder/sections/shared/TimeZoneText.vue'
import { getRgbaColor } from '@/lib/calendesk-js-library/filters/getRgbaColor'
import { RoundType } from '@/calendesk/models/RoundType'
import { EmployeeSelectionModeType } from '@/calendesk/models/EmployeeSelectionModeType'

export default mixins(Section).extend({
  name: 'CalendarV3',
  components: {
    TimeZoneText
  },
  computed: {
    primaryColor (): string {
      return this.draftConfiguration.wb_color_primary__color__
    },
    v3Radius (): { widget: string; element: string } {
      // Mirrors the Vuetify rounded-* scale that classForViewRound applies to v-cards.
      switch (this.viewRoundType) {
        case RoundType.ZERO:
          return { widget: '0', element: '0' }
        case RoundType.SM:
          return { widget: '2px', element: '2px' }
        case RoundType.LG:
          return { widget: '8px', element: '8px' }
        case RoundType.XL:
          return { widget: '16px', element: '12px' }
        case RoundType.PILL:
        case RoundType.CIRCLE:
          return { widget: '24px', element: '9999px' }
        default:
          return { widget: '4px', element: '4px' }
      }
    },
    widgetStyleVars (): Record<string, string> {
      const textBase = this.textColor1 || this.primaryColor
      const sectionHeight = Number(this.data.configuration.wb_height__style_height__) || 700

      return {
        '--v3-surface': this.backgroundColor1,
        '--v3-p04': getRgbaColor(this.primaryColor, 0.04),
        '--v3-p08': getRgbaColor(this.primaryColor, 0.08),
        '--v3-border-soft': getRgbaColor(textBase, 0.12),
        '--v3-radius-widget': this.v3Radius.widget,
        '--v3-radius': this.v3Radius.element,
        '--v3-max-height': `${Math.max(480, sectionHeight - 60)}px`,
        '--v3-shadow-widget': this.data.configuration.wb_view_flat__checkbox__
          ? 'none'
          : `0 2px 6px ${getRgbaColor(textBase, 0.05)}, 0 12px 32px ${getRgbaColor(textBase, 0.09)}`
      }
    },
    railSteps (): Array<Record<string, any>> {
      const configured = this.data.configuration.wb_selection_order__select__?.value as string | undefined
      const order = (configured || 'service_location_employee').split('_')

      const stepsByKey: Record<string, Record<string, any>> = {
        service: {
          label: this.$trans('service'),
          value: this.$trans('wb_dummy_text_service')
        },
        location: {
          label: this.$trans('service_location'),
          value: this.$trans('wb_dummy_text_address')
        },
        employee: {
          label: this.$trans('employee'),
          // The auto-assign mode pre-fills the step with "any person"
          // on the client; the rail preview mirrors that resolved value.
          value: this.employeeSelectionMode === EmployeeSelectionModeType.AUTO_ASSIGN
            ? this.$trans('any_person_label')
            : this.$trans('wb_dummy_text_reviews_full_name')
        }
      }

      const steps: Array<Record<string, any>> = order
        .filter((key: string) => stepsByKey[key] && !(key === 'employee' && this.areEmployeesHidden))
        .map((key: string, index: number) => ({
          position: index + 1,
          ...stepsByKey[key],
          done: true,
          current: false
        }))

      steps.push({
        position: steps.length + 1,
        label: this.$trans('date'),
        value: null,
        placeholder: this.$trans('wb_dummy_text_select_date'),
        done: false,
        current: true
      })

      return steps
    },
    getMonthLabel (): string {
      return this.$moment().format('MMMM YYYY')
    },
    getWeekdayLabels (): Array<string> {
      const labels = []
      const monday = this.$moment().startOf('isoWeek')

      for (let i = 0; i < 7; i++) {
        labels.push(monday.clone().add(i, 'days').format('dd'))
      }

      return labels
    },
    getGridOffset (): number {
      return this.$moment().startOf('month').isoWeekday() - 1
    },
    getGridDays (): Array<Record<string, any>> {
      const days = []
      const today = this.$moment().date()
      const daysInMonth = this.$moment().daysInMonth()

      for (let dayNumber = 1; dayNumber <= daysInMonth; dayNumber++) {
        const isPast = dayNumber < today
        days.push({
          dayNumber,
          disabled: isPast || dayNumber % 7 === 0,
          selected: dayNumber === Math.min(today + 2, daysInMonth)
        })
      }

      return days
    },
    getSelectedDayLabel (): string {
      const daysInMonth = this.$moment().daysInMonth()
      const selectedDay = Math.min(this.$moment().date() + 2, daysInMonth)
      return this.$moment().date(selectedDay).format('dddd, D MMMM')
    },
    previewSlots (): Array<string> {
      return ['09:00', '10:30', '12:15', '14:30', '16:00', '17:30']
    }
  }
})
